import { buttonBaseStyle, buttonStyle } from './Button.styles';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { SuperElement } from '../../internals/super/SuperElement';
import { html, unsafeStatic } from 'lit/static-html.js';
import { event, type EventDispatcher } from '../../utils/event';
import '../spinner';
import { type Icons } from '../icon';
import { submit } from '../../utils/form-helpers';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'tertiary-transparent' | 'link';
export type ButtonSize = 'big' | 'medium' | 'small';
export type ButtonIconPosition = 'left' | 'right';
export type ButtonType = 'submit' | 'button' | 'reset';

export class ButtonBase extends SuperElement {
    static formAssociated = true;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    @property({ type: String, reflect: true })
    size: ButtonSize = 'medium';

    @property({ type: String })
    icon?: Icons;

    @property({ type: String, reflect: true })
    iconPosition?: ButtonIconPosition = 'left';

    @property({ type: String, reflect: true })
    type?: ButtonType = 'button';

    @property({ type: Boolean, reflect: true })
    reversed = false;

    @property({ type: String })
    href?: string;

    @property({ type: String })
    target?: string = '_self';

    @property({ type: Boolean, reflect: true })
    loading = false;

    @property({ type: String })
    loadingText?: string;

    @event('on-click')
    private clickEventDispatcher!: EventDispatcher<string>;

    static styles = [SuperElement.styles, buttonBaseStyle];
    private internals: ElementInternals;

    constructor() {
        super();
        this.internals = this.attachInternals();
    }

    get _hasDefaultSlot() {
        const childNodes = [...Array.from(this.childNodes ?? [])];

        return childNodes.some((node) => {
            const nodeType = node.nodeType;

            // has only text node.
            if (nodeType === node.TEXT_NODE && node.textContent?.trim() !== '') {
                return true;
            }
            // has element node, it should not have slot attribute.
            if (nodeType === node.ELEMENT_NODE) {
                if (!(node as HTMLElement).hasAttribute('slot')) {
                    return true;
                }
            }
            return false;
        });
    }

    protected async handleClick() {
        this.clickEventDispatcher('Click event fired!');
        const { form } = this.internals;
        if (form) submit(form);
    }

    renderElement() {
        const isAnchor = !!this.href;
        const isDisabled = this.disabled;
        const classes = classMap({
            button: true,
            'has-content': this._hasDefaultSlot
        });
        const text = this.loading && this.loadingText ? this.loadingText : html`<slot></slot>`;
        const iconComponent = this.icon
            ? html`<${unsafeStatic(`lr-icon-${this.icon}`)} class="icon" size=${this.size}></${unsafeStatic(
                  `lr-icon-${this.icon}`
              )}>`
            : null;
        const loadingIcon = html`<lr-spinner class="loading-spinner" size="${this.size}"></lr-spinner>`;
        const slots = html`<slot name="icon">${this.loading ? loadingIcon : iconComponent ?? ''}</slot>
            <span class="text">${text}</span>`;

        return isAnchor
            ? html`<a
                  aria-disabled=${isDisabled}
                  class=${classes}
                  href=${ifDefined(this.href)}
                  target=${ifDefined(this.target)}
                  role="button"
                  part="base"
                  @click=${this.handleClick}
                  >${slots}
              </a>`
            : html`<button
                  aria-disabled=${ifDefined(isDisabled)}
                  ?disabled=${isDisabled}
                  class=${classes}
                  part="base"
                  @click=${this.handleClick}
              >
                  ${slots}
              </button>`;
    }
}
@customElement('lr-button')
export class Button extends ButtonBase {
    @property({ type: String })
    variant: ButtonVariant = 'primary';

    @property({ type: Boolean })
    resetStyle?: boolean;

    @property({ type: Boolean })
    reversed = false;

    @property({
        reflect: true,
        converter: {
            fromAttribute(v) {
                if (!v) return false;
                else if (v.toString() === 'true') return true;
                else if (v.toString() === 'only-mobile') return 'only-mobile';
                else return false;
            }
        }
    })
    fluid: boolean | 'only-mobile' = false;
    static styles = [ButtonBase.styles, buttonStyle];
}

declare global {
    interface HTMLElementTagNameMap {
        'lr-button': Button;
    }
}
